import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import {
  cfeHero,
  cfeOverview,
  cfeNav,
  cfePathwayPrompts,
  cfePathways,
  cfeCoopNote,
  cfeRoadmapIntro,
  cfeRoadmap,
  cfeGovernanceIntro,
  cfeGovernanceOptions,
  cfeLegalDistinctions,
  cfeGovernanceQuestions,
  cfeIndigenousNote,
  cfeCompliance,
  cfeNationalDistinction,
  cfeFundingPrinciple,
  cfeFundingSources,
  cfeFundingStages,
  cfeFundingCategories,
  cfeFundingCategoriesNote,
  cfeReadinessPackage,
  cfeFundingRegister,
  cfeToolsNote,
  cfeBreakEven,
  cfeFundingGap,
  cfeChecklist,
  cfeScenarios,
  cfeOutcomes,
  cfeFaqs,
  cfeNationalResources,
  cfeJurisdictions,
  cfeClosing,
} from "@/content/communityFood";

const scrollToAnchor = (anchor) => {
  const el = document.getElementById(anchor);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Accessible accordion: panels stay in the DOM so print output is complete. */
const Accordion = ({ id, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-charcoal/10">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={`${id}-button`}
        data-testid={`${id}-button`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-baseline justify-between gap-4 py-5 text-left focus-visible:outline-2 focus-visible:outline-ochre"
      >
        <span className="font-serif text-lg md:text-xl text-charcoal leading-snug">{title}</span>
        <span aria-hidden="true" className="text-ochre text-xl shrink-0">{open ? "−" : "+"}</span>
      </button>
      <div
        id={`${id}-panel`}
        role="region"
        aria-labelledby={`${id}-button`}
        className={`cfe-accordion-panel ${open ? "block" : "hidden"} pb-6`}
        data-testid={`${id}-panel`}
      >
        {children}
      </div>
    </div>
  );
};

const ExternalLink = ({ href, children, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
  >
    {children}
    <span className="sr-only"> (opens in a new tab)</span>
  </a>
);

const num = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmt = (n) =>
  n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 });

// --- Break-even calculator -----------------------------------------------------

const BreakEvenCalculator = () => {
  const d = cfeBreakEven.defaults;
  const [fixed, setFixed] = useState(String(d.fixed));
  const [price, setPrice] = useState(String(d.price));
  const [variable, setVariable] = useState(String(d.variable));
  const [subsidy, setSubsidy] = useState(String(d.subsidy));

  const vals = [num(fixed), num(price), num(variable), num(subsidy)];
  const invalid = vals.some((v) => v === null || v < 0);
  const contribution = invalid ? null : vals[1] - vals[2];
  const nonPositive = !invalid && contribution <= 0;
  const netFixed = invalid || nonPositive ? null : Math.max(0, vals[0] - vals[3]);
  const required = netFixed === null ? null : Math.ceil(netFixed / contribution);
  const weekly = required === null ? null : Math.ceil((required * 12) / 52);

  const Field = ({ label, value, set, testid }) => (
    <label className="block">
      <span className="text-sm text-charcoal/85">{label}</span>
      <input
        type="number"
        min="0"
        inputMode="decimal"
        value={value}
        onChange={(e) => set(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full border border-charcoal/25 bg-cream px-3 py-2.5 text-base text-charcoal focus-visible:outline-2 focus-visible:outline-ochre"
      />
    </label>
  );

  return (
    <div className="border border-charcoal/15 bg-sage/40 p-6 md:p-8" data-testid="breakeven-calculator">
      <h3 className="font-serif text-xl md:text-2xl text-charcoal">{cfeBreakEven.heading}</h3>
      <p className="mt-2 text-xs text-charcoal/60 italic">{cfeBreakEven.note}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Monthly fixed costs ($)" value={fixed} set={setFixed} testid="breakeven-fixed" />
        <Field label="Selling price per unit ($)" value={price} set={setPrice} testid="breakeven-price" />
        <Field label="Variable cost per unit ($)" value={variable} set={setVariable} testid="breakeven-variable" />
        <Field label="Committed monthly operating subsidy ($)" value={subsidy} set={setSubsidy} testid="breakeven-subsidy" />
      </div>
      <div aria-live="polite" className="mt-6" data-testid="breakeven-result">
        {invalid && (
          <p className="text-sm text-charcoal/80 border-l-2 border-ochre pl-4">
            Enter a valid, non-negative number in each field to see the result.
          </p>
        )}
        {!invalid && nonPositive && (
          <p className="text-sm text-charcoal/80 border-l-2 border-ochre pl-4" data-testid="breakeven-warning">
            The selling price must be higher than the variable cost per unit. This price/cost model needs
            redesign before break-even can be reached by volume alone.
          </p>
        )}
        {!invalid && !nonPositive && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">Contribution per unit</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="breakeven-contribution">{fmt(contribution)}</p>
            </div>
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">Required sales</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="breakeven-required">
                {required.toLocaleString("en-AU")} units / month
              </p>
              {required === 0 && (
                <p className="mt-1 text-xs text-charcoal/60">
                  The committed subsidy covers fixed costs under this simplified model; contribution still
                  matters for surplus.
                </p>
              )}
            </div>
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">Approx. weekly equivalent</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="breakeven-weekly">
                {weekly.toLocaleString("en-AU")} units
              </p>
            </div>
          </div>
        )}
      </div>
      <p className="mt-4 text-xs text-charcoal/60">{cfeBreakEven.limits}</p>
    </div>
  );
};

// --- Funding gap calculator ----------------------------------------------------

const FundingGapCalculator = () => {
  const [uses, setUses] = useState({ establishment: "", equipment: "", working: "", contingency: "" });
  const [sources, setSources] = useState({ unrestricted: "", eligible: "" });

  const usesVals = [num(uses.establishment), num(uses.equipment), num(uses.working), num(uses.contingency)];
  const srcVals = [num(sources.unrestricted), num(sources.eligible)];
  const anyInput = [...Object.values(uses), ...Object.values(sources)].some((v) => v !== "");
  const invalid = [...usesVals, ...srcVals].some((v) => v !== null && v < 0) ||
    (anyInput && usesVals.some((v) => v === null)) ||
    (anyInput && srcVals.some((v) => v === null));

  const totalUses = usesVals.reduce((a, v) => a + (v || 0), 0);
  const totalSources = srcVals.reduce((a, v) => a + (v || 0), 0);
  const gap = Math.max(0, totalUses - totalSources);
  const surplus = Math.max(0, totalSources - totalUses);

  const Field = ({ label, value, onChange, testid }) => (
    <label className="block">
      <span className="text-sm text-charcoal/85">{label}</span>
      <input
        type="number"
        min="0"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full border border-charcoal/25 bg-cream px-3 py-2.5 text-base text-charcoal focus-visible:outline-2 focus-visible:outline-ochre"
      />
    </label>
  );

  return (
    <div className="border border-charcoal/15 bg-sage/40 p-6 md:p-8" data-testid="funding-gap-calculator">
      <h3 className="font-serif text-xl md:text-2xl text-charcoal">{cfeFundingGap.heading}</h3>
      <p className="mt-2 text-xs text-charcoal/60 italic">{cfeFundingGap.note}</p>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <Field label="One-off establishment costs ($)" value={uses.establishment} onChange={(v) => setUses({ ...uses, establishment: v })} testid="gap-establishment" />
        <Field label="Equipment / fit-out ($)" value={uses.equipment} onChange={(v) => setUses({ ...uses, equipment: v })} testid="gap-equipment" />
        <Field label="Working capital allowance ($)" value={uses.working} onChange={(v) => setUses({ ...uses, working: v })} testid="gap-working" />
        <Field label="Contingency ($)" value={uses.contingency} onChange={(v) => setUses({ ...uses, contingency: v })} testid="gap-contingency" />
        <Field label="Confirmed unrestricted cash available ($)" value={sources.unrestricted} onChange={(v) => setSources({ ...sources, unrestricted: v })} testid="gap-unrestricted" />
        <Field label="Confirmed funding eligible for these costs ($)" value={sources.eligible} onChange={(v) => setSources({ ...sources, eligible: v })} testid="gap-eligible" />
      </div>
      <div aria-live="polite" className="mt-6" data-testid="gap-result">
        {invalid && (
          <p className="text-sm text-charcoal/80 border-l-2 border-ochre pl-4">
            Complete every field with a valid, non-negative number to see the result.
          </p>
        )}
        {!invalid && anyInput && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">Total cash uses</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="gap-uses">{fmt(totalUses)}</p>
            </div>
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">Confirmed cash sources</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="gap-sources">{fmt(totalSources)}</p>
            </div>
            <div className="border border-charcoal/15 bg-cream p-4">
              <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">{surplus > 0 ? "Surplus" : "Funding gap"}</p>
              <p className="mt-1 font-serif text-2xl text-charcoal" data-testid="gap-outcome">
                {fmt(surplus > 0 ? surplus : gap)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Readiness checklist ---------------------------------------------------------

const STORAGE_KEY = "cfe-readiness";

const ReadinessChecklist = () => {
  const [checked, setChecked] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      /* storage unavailable - checklist still works in memory */
    }
    return {};
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(checked));
    } catch {
      /* storage unavailable - ignore */
    }
  }, [checked]);

  const done = cfeChecklist.items.filter((_, i) => checked[i]).length;

  const download = () => {
    const lines = cfeChecklist.items.map((item, i) => `${checked[i] ? "[x]" : "[ ]"} ${item}`);
    const blob = new Blob([`Community Food Enterprise - readiness checklist\n${done} of ${cfeChecklist.items.length} planning items addressed\n\n${lines.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "community-food-enterprise-readiness.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border border-charcoal/15 bg-cream p-6 md:p-8" data-testid="readiness-checklist">
      <p className="text-sm text-charcoal/70" data-testid="readiness-progress" aria-live="polite">
        {done} of {cfeChecklist.items.length} planning items addressed
      </p>
      <div className="mt-2 h-1.5 bg-charcoal/10" aria-hidden="true">
        <div className="h-full bg-ochre transition-all duration-300" style={{ width: `${(done / cfeChecklist.items.length) * 100}%` }} />
      </div>
      <ul className="mt-6 space-y-3">
        {cfeChecklist.items.map((item, i) => (
          <li key={item}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => setChecked({ ...checked, [i]: !checked[i] })}
                data-testid={`readiness-item-${i + 1}`}
                className="mt-1 h-4 w-4 accent-eucalyptus"
              />
              <span className={`text-sm md:text-base leading-relaxed ${checked[i] ? "text-charcoal/50 line-through" : "text-charcoal/90"}`}>
                {item}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap gap-4">
        <button
          type="button"
          onClick={() => setChecked({})}
          data-testid="readiness-reset"
          className="border border-eucalyptus text-eucalyptus px-6 py-2.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={download}
          data-testid="readiness-download"
          className="border border-eucalyptus text-eucalyptus px-6 py-2.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
        >
          Download my checklist
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="readiness-print"
          className="border border-eucalyptus text-eucalyptus px-6 py-2.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
        >
          Print
        </button>
      </div>
    </div>
  );
};

// --- Page -------------------------------------------------------------------------

export default function CommunityFood() {
  const [jurisdiction, setJurisdiction] = useState(null);

  return (
    <>
      <Seo
        title="Community Food Enterprise"
        description="Practical pathways to establish, fund and sustain a community food enterprise in Australia - pathways, establishment stages, governance, compliance, funding and planning tools."
        path="/our-work/community-food-enterprise"
      />
      <main className="pb-24 overflow-x-hidden">
        {/* Hero */}
        <section className="bg-eucalyptus text-cream">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <Reveal>
              <nav aria-label="Breadcrumb" className="text-xs tracking-wide text-cream/60" data-testid="cfe-breadcrumb">
                <Link to="/" className="hover:text-cream transition-colors duration-300">Home</Link>
                <span aria-hidden="true"> → </span>
                <Link to="/our-work" className="hover:text-cream transition-colors duration-300">Our Work</Link>
                <span aria-hidden="true"> → </span>
                <span className="text-cream/85">Community Food Enterprise</span>
              </nav>
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-ochre" data-testid="cfe-eyebrow">
                {cfeHero.eyebrow}
              </p>
              <h1 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-tight max-w-3xl">
                {cfeHero.heading}
              </h1>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-cream/85 max-w-2xl">
                {cfeHero.intro}
              </p>
              <p className="mt-4 font-serif italic text-lg text-ochre max-w-2xl">{cfeHero.supporting}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => scrollToAnchor(cfeHero.primaryCta.anchor)}
                  data-testid="cfe-hero-primary-cta"
                  className="bg-cream text-eucalyptus px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-ochre hover:text-charcoal transition-colors duration-300"
                >
                  {cfeHero.primaryCta.label}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToAnchor(cfeHero.secondaryCta.anchor)}
                  data-testid="cfe-hero-secondary-cta"
                  className="border border-cream/60 text-cream px-8 py-3.5 rounded-sm text-sm tracking-wide hover:bg-cream hover:text-eucalyptus transition-colors duration-300"
                >
                  {cfeHero.secondaryCta.label}
                </button>
              </div>
            </Reveal>
          </div>
        </section>

        {/* On-page navigation */}
        <nav aria-label="On this page" className="border-b border-charcoal/10 bg-cream">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex flex-wrap gap-x-6 gap-y-2" data-testid="cfe-nav">
            {cfeNav.map((n) => (
              <button
                key={n.anchor}
                type="button"
                onClick={() => scrollToAnchor(n.anchor)}
                data-testid={`cfe-nav-${n.anchor}`}
                className="text-xs uppercase tracking-[0.12em] text-charcoal/70 hover:text-ochre-dark transition-colors duration-300"
              >
                {n.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Overview */}
        <section id="overview" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-overview-heading">
              Overview
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">{cfeOverview.definition}</p>
            <div className="mt-6 bg-sage/50 border-l-2 border-ochre p-6" data-testid="cfe-reality-check">
              <p className="text-sm md:text-base leading-relaxed text-charcoal/90">{cfeOverview.realityCheck}</p>
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.15em] text-ochre-dark">Three questions to carry through</p>
            <ol className="mt-3 space-y-2.5 list-decimal pl-5 text-base leading-relaxed text-charcoal/90" data-testid="cfe-three-questions">
              {cfeOverview.questions.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ol>
            <p className="mt-6 text-base leading-relaxed text-charcoal/90">{cfeOverview.notEveryNote}</p>
          </Reveal>
        </section>

        {/* Choose a pathway */}
        <section id="choose-a-pathway" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-pathways-heading">
              Choose an enterprise pathway
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90">
              Explore options side by side - several pathways can be considered together. No option here declares a
              legally suitable structure or guarantees feasibility.
            </p>
          </Reveal>
          <div className="mt-8 border-t border-charcoal/10" data-testid="cfe-pathways-list">
            {cfePathways.map((p, i) => (
              <Accordion key={p.name} id={`cfe-pathway-${i + 1}`} title={p.name}>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm leading-relaxed">
                  <div>
                    <dt className="text-xs uppercase tracking-[0.12em] text-ochre-dark">Core activity</dt>
                    <dd className="mt-1 text-charcoal/90">{p.activity}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.12em] text-ochre-dark">Possible income</dt>
                    <dd className="mt-1 text-charcoal/90">{p.income}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.12em] text-ochre-dark">Early pilot</dt>
                    <dd className="mt-1 text-charcoal/90">{p.pilot}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-[0.12em] text-ochre-dark">Main issues to test</dt>
                    <dd className="mt-1 text-charcoal/90">{p.issues}</dd>
                  </div>
                </dl>
                <ul className="mt-5 space-y-1.5 text-sm text-charcoal/85 list-disc pl-5">
                  {cfePathwayPrompts.map((prompt) => (
                    <li key={prompt}>{prompt}</li>
                  ))}
                </ul>
              </Accordion>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 text-sm text-charcoal/70 border-l-2 border-ochre pl-4" data-testid="cfe-coop-note">
              {cfeCoopNote}
            </p>
          </Reveal>
        </section>

        {/* Establish */}
        <section id="establish" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-roadmap-heading">
              Establishment roadmap: staged decision gates
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90">{cfeRoadmapIntro}</p>
          </Reveal>
          <div className="mt-8 border-t border-charcoal/10" data-testid="cfe-roadmap-list">
            {cfeRoadmap.map((s, i) => (
              <Accordion key={s.letter} id={`cfe-stage-${s.letter.toLowerCase()}`} title={`Stage ${s.letter} - ${s.title}`}>
                <p className="text-sm md:text-base leading-relaxed text-charcoal/90">{s.body}</p>
                <p className="mt-4 text-xs uppercase tracking-[0.12em] text-ochre-dark">Outputs</p>
                <ul className="mt-2 space-y-1.5 text-sm text-charcoal/85 list-disc pl-5">
                  {s.outputs.map((o) => (
                    <li key={o}>{o}</li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-charcoal/80 bg-sage/50 border-l-2 border-ochre pl-4 py-2 pr-3" data-testid={`cfe-stage-gate-${i + 1}`}>
                  {s.gate}
                </p>
              </Accordion>
            ))}
          </div>
        </section>

        {/* Governance */}
        <section id="governance" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-governance-heading">
              Ownership, governance and legal pathways
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90 max-w-3xl">{cfeGovernanceIntro}</p>
          </Reveal>
          <Reveal className="mt-8">
            <div className="w-full max-w-full overflow-x-auto border border-charcoal/15" role="region" aria-label="Legal structure comparison" tabIndex={0}>
              <table className="w-full min-w-[640px] text-sm text-left" data-testid="cfe-governance-table">
                <thead className="bg-sage/50 text-charcoal">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Option</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Potential fit</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Key checks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal/10">
                  {cfeGovernanceOptions.map((g) => (
                    <tr key={g.option} className="align-top">
                      <td className="px-4 py-4 font-medium text-charcoal">{g.option}</td>
                      <td className="px-4 py-4 text-charcoal/85">{g.fit}</td>
                      <td className="px-4 py-4 text-charcoal/85">{g.checks}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal className="mt-8">
            <div className="bg-eucalyptus text-cream p-6 md:p-8" data-testid="cfe-legal-distinctions">
              {cfeLegalDistinctions.map((d) => (
                <p key={d.slice(0, 20)} className="text-base leading-relaxed text-cream/90 mt-3 first:mt-0">
                  {d}
                </p>
              ))}
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-10">
            <Reveal>
              <h3 className="font-serif text-xl md:text-2xl text-charcoal">Governance questions to settle</h3>
              <ul className="mt-4 space-y-1.5 text-sm md:text-base text-charcoal/85 list-disc pl-5" data-testid="cfe-governance-questions">
                {cfeGovernanceQuestions.map((q) => (
                  <li key={q}>{q}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.06}>
              <h3 className="font-serif text-xl md:text-2xl text-charcoal">First Nations foods, seeds and knowledge</h3>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/90">{cfeIndigenousNote}</p>
            </Reveal>
          </div>
        </section>

        {/* Compliance */}
        <section id="compliance" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-compliance-heading">
              Australian compliance map
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90">
              An expandable checklist of the areas to confirm for your location and activity. Fees, thresholds and
              exemptions vary - verify each against the current official requirements.
            </p>
          </Reveal>
          <div className="mt-8 border-t border-charcoal/10" data-testid="cfe-compliance-list">
            {cfeCompliance.map((cItem, i) => (
              <Accordion key={cItem.area} id={`cfe-compliance-${i + 1}`} title={cItem.area}>
                <p className="text-sm md:text-base leading-relaxed text-charcoal/90">{cItem.detail}</p>
              </Accordion>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 text-sm text-charcoal/70 border-l-2 border-ochre pl-4" data-testid="cfe-national-distinction">
              {cfeNationalDistinction}
            </p>
          </Reveal>
        </section>

        {/* Funding */}
        <section id="funding" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-funding-heading">
              Funding pathways
            </h2>
            <p className="mt-4 font-serif italic text-lg text-charcoal/85 max-w-3xl" data-testid="cfe-funding-principle">
              {cfeFundingPrinciple}
            </p>
          </Reveal>
          <Reveal className="mt-8">
            <div className="w-full max-w-full overflow-x-auto border border-charcoal/15" role="region" aria-label="Funding source comparison" tabIndex={0}>
              <table className="w-full min-w-[760px] text-sm text-left" data-testid="cfe-funding-table">
                <thead className="bg-sage/50 text-charcoal">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Source</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Potential use</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">What to prepare</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Obligations and limitations</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal/10">
                  {cfeFundingSources.map((f) => (
                    <tr key={f.source} className="align-top">
                      <td className="px-4 py-4 font-medium text-charcoal">{f.source}</td>
                      <td className="px-4 py-4 text-charcoal/85">{f.use}</td>
                      <td className="px-4 py-4 text-charcoal/85">{f.prepare}</td>
                      <td className="px-4 py-4 text-charcoal/85">{f.obligations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12">
            <Reveal>
              <h3 className="font-serif text-xl md:text-2xl text-charcoal">Pathways by development stage</h3>
              <ul className="mt-4 space-y-4" data-testid="cfe-funding-stages">
                {cfeFundingStages.map((s) => (
                  <li key={s.stage} className="border-l-2 border-ochre pl-4">
                    <p className="font-medium text-charcoal">{s.stage}</p>
                    <p className="mt-1 text-sm leading-relaxed text-charcoal/85">{s.text}</p>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.06}>
              <h3 className="font-serif text-xl md:text-2xl text-charcoal">Categories worth researching</h3>
              <ul className="mt-4 space-y-1.5 text-sm md:text-base text-charcoal/85 list-disc pl-5" data-testid="cfe-funding-categories">
                {cfeFundingCategories.map((cat) => (
                  <li key={cat}>{cat}</li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-charcoal/70 italic">{cfeFundingCategoriesNote}</p>
            </Reveal>
          </div>

          <Reveal className="mt-12">
            <h3 className="font-serif text-xl md:text-2xl text-charcoal">What a funding-ready package includes</h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm md:text-base text-charcoal/85 list-disc pl-5" data-testid="cfe-readiness-package">
              {cfeReadinessPackage.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-12">
            <h3 className="font-serif text-xl md:text-2xl text-charcoal" data-testid="cfe-register-heading">
              Funding register
            </h3>
            <p className="mt-2 text-xs text-charcoal/60 italic">
              Verified {cfeFundingRegister.verified}. {cfeFundingRegister.note}
            </p>
            <div className="mt-4 w-full max-w-full overflow-x-auto border border-charcoal/15" role="region" aria-label="Funding register" tabIndex={0}>
              <table className="w-full min-w-[720px] text-sm text-left" data-testid="cfe-funding-register">
                <thead className="bg-sage/50 text-charcoal">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Name</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Provider</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Scope</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Support type</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Status</th>
                    <th scope="col" className="px-4 py-3 font-serif text-base">Official page</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-charcoal/10">
                  {cfeFundingRegister.entries.map((e) => (
                    <tr key={e.name} className="align-top">
                      <td className="px-4 py-4 font-medium text-charcoal">{e.name}</td>
                      <td className="px-4 py-4 text-charcoal/85">{e.provider}</td>
                      <td className="px-4 py-4 text-charcoal/85">{e.scope}</td>
                      <td className="px-4 py-4 text-charcoal/85">{e.type}</td>
                      <td className="px-4 py-4 text-charcoal/85">{e.status}</td>
                      <td className="px-4 py-4">
                        <ExternalLink href={e.url}>Check current availability</ExternalLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </section>

        {/* Check the numbers */}
        <section id="check-the-numbers" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-tools-heading">
              Check the numbers
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90 max-w-3xl">{cfeToolsNote}</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal><BreakEvenCalculator /></Reveal>
            <Reveal delay={0.06}><FundingGapCalculator /></Reveal>
          </div>
        </section>

        {/* Readiness */}
        <section id="readiness" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-readiness-heading">
              {cfeChecklist.heading}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90">{cfeChecklist.intro}</p>
          </Reveal>
          <Reveal className="mt-8">
            <ReadinessChecklist />
          </Reveal>
        </section>

        {/* Scenarios */}
        <section id="scenarios" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-scenarios-heading">
              Illustrative scenarios
            </h2>
            <p className="mt-3 text-xs uppercase tracking-[0.15em] text-ochre-dark" data-testid="cfe-scenarios-label">
              {cfeScenarios.label}
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {cfeScenarios.items.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05} className="h-full">
                <div className="h-full border border-charcoal/15 bg-cream p-7" data-testid={`cfe-scenario-${i + 1}`}>
                  <h3 className="font-serif text-xl md:text-2xl text-charcoal">{s.title}</h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-charcoal/90">{s.body}</p>
                  <ul className="mt-4 space-y-1.5 text-sm text-charcoal/85 list-disc pl-5">
                    <li>{s.test}</li>
                    <li>{s.funding}</li>
                    <li>{s.decision}</li>
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Outcomes */}
        <section id="outcomes" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-outcomes-heading">
              Outcomes and accountability
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90 max-w-3xl">{cfeOutcomes.intro}</p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {cfeOutcomes.groups.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.04} className="h-full">
                <div className="h-full border border-charcoal/15 bg-cream p-6" data-testid={`cfe-outcome-${i + 1}`}>
                  <h3 className="font-serif text-lg text-charcoal">{g.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/85">{g.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section id="faqs" className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-faqs-heading">
              Questions people ask
            </h2>
          </Reveal>
          <div className="mt-8 border-t border-charcoal/10" data-testid="cfe-faqs-list">
            {cfeFaqs.map((f, i) => (
              <Accordion key={f.q} id={`cfe-faq-${i + 1}`} title={f.q}>
                <p className="text-sm md:text-base leading-relaxed text-charcoal/90">{f.a}</p>
              </Accordion>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section id="resources" className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-20 scroll-mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-resources-heading">
              Official resources
            </h2>
            <p className="mt-4 text-base leading-relaxed text-charcoal/90 max-w-3xl">
              National starting points, shown for all visitors. These are research routes, not endorsements or
              partnerships - verify current requirements on each official page.
            </p>
          </Reveal>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4" data-testid="cfe-national-resources">
            {cfeNationalResources.map((r) => (
              <li key={r.name}>
                <ExternalLink href={r.url}>{r.name}</ExternalLink>
                <p className="mt-0.5 text-xs text-charcoal/60">{r.provider}</p>
              </li>
            ))}
          </ul>

          <Reveal className="mt-10">
            <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark">From our library</p>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-charcoal/90 max-w-2xl" data-testid="cfe-change-handbook-link-para">
              Community food enterprises depend on people shaping change together.{" "}
              <Link
                to="/resources#featured-entry-the-change-handbook"
                data-testid="cfe-change-handbook-link"
                className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                The Change Handbook
              </Link>{" "}
              brings together more than 60 methods for designing participatory gatherings and turning shared ideas into sustained action.
            </p>
          </Reveal>

          <Reveal className="mt-12">
            <h3 className="font-serif text-xl md:text-2xl text-charcoal">State and territory routes</h3>
            <p className="mt-2 text-sm text-charcoal/70 max-w-2xl">
              Select a state or territory for food regulation, business and grants support, and association or
              co-operative registration routes. National resources above apply everywhere.
            </p>
            <div role="group" aria-label="Select a state or territory" className="mt-4 flex flex-wrap gap-2" data-testid="cfe-jurisdiction-selector">
              {cfeJurisdictions.map((j) => (
                <button
                  key={j.id}
                  type="button"
                  aria-pressed={jurisdiction === j.id}
                  onClick={() => setJurisdiction(jurisdiction === j.id ? null : j.id)}
                  data-testid={`cfe-jurisdiction-${j.id.toLowerCase()}`}
                  className={`px-4 py-2 border text-sm tracking-wide transition-colors duration-300 ${
                    jurisdiction === j.id
                      ? "bg-eucalyptus text-cream border-eucalyptus"
                      : "border-charcoal/25 text-charcoal/80 hover:border-ochre"
                  }`}
                >
                  {j.id}
                </button>
              ))}
              {jurisdiction && (
                <button
                  type="button"
                  onClick={() => setJurisdiction(null)}
                  data-testid="cfe-jurisdiction-reset"
                  className="px-4 py-2 text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4"
                >
                  Reset
                </button>
              )}
            </div>
            {jurisdiction ? (
              <ul className="mt-6 space-y-3" data-testid="cfe-jurisdiction-links">
                {["food", "business", "entity"].map((key) => {
                  const j = cfeJurisdictions.find((x) => x.id === jurisdiction);
                  const item = j[key];
                  return (
                    <li key={key}>
                      <ExternalLink href={item.url} testid={`cfe-jurisdiction-link-${key}`}>{item.label}</ExternalLink>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-6 text-sm text-charcoal/60 italic" data-testid="cfe-jurisdiction-empty">
                No state or territory selected - national resources are shown above.
              </p>
            )}
          </Reveal>
        </section>

        {/* Closing CTA */}
        <section className="max-w-3xl mx-auto px-6 md:px-10 pt-20 md:pt-24 text-center">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="cfe-closing-heading">
              {cfeClosing.heading}
            </h2>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">{cfeClosing.body}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              {cfeClosing.ctas.map((cta) =>
                cta.action === "print" ? (
                  <button
                    key={cta.label}
                    type="button"
                    onClick={() => window.print()}
                    data-testid="cfe-cta-print"
                    className="border border-eucalyptus text-eucalyptus px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
                  >
                    {cta.label}
                  </button>
                ) : (
                  <button
                    key={cta.label}
                    type="button"
                    onClick={() => scrollToAnchor(cta.anchor)}
                    data-testid={`cfe-cta-${cta.anchor}`}
                    className="bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
                  >
                    {cta.label}
                  </button>
                )
              )}
            </div>
            <p className="mt-8 text-sm text-charcoal/70">
              {cfeClosing.contactNote.before}
              <Link
                to={cfeClosing.contactNote.href}
                data-testid="cfe-contact-link"
                className="text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
              >
                {cfeClosing.contactNote.linkText}
              </Link>
            </p>
          </Reveal>
        </section>
      </main>
    </>
  );
}
